import { injectable } from 'tsyringe';
import { readCsrfToken } from '@/shared/auth/CsrfToken';
import type { DashboardComposeRequest, DashboardStreamFrame } from '@/shared/assist/DashboardContract';
import type { AIExplorerStreamHandle, AIExplorerStreamHandlers, IAIExplorerStreamSource } from './AIExplorerStreamSource';

const STREAM_PATH = '/_assist/stream';

/**
 * Streams a composed dashboard from the BFF over a websocket. The request is sent
 * as the first message (a browser can't set headers on a ws upgrade) carrying the
 * CSRF token so the BFF can authenticate the session; the session cookies ride
 * along on the same-origin upgrade. Each subsequent message is one
 * {@link DashboardStreamFrame}.
 */
@injectable()
export class WebSocketAIExplorerStreamSource implements IAIExplorerStreamSource {
    open(request: DashboardComposeRequest, handlers: AIExplorerStreamHandlers): AIExplorerStreamHandle {
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const socket = new WebSocket(`${proto}://${window.location.host}${STREAM_PATH}`);
        let active = true;

        socket.onopen = (): void => {
            const payload: DashboardComposeRequest = { ...request, csrf: readCsrfToken() };
            socket.send(JSON.stringify(payload));
        };
        socket.onmessage = (event: MessageEvent): void => {
            if (!active) return;
            const frame = parseFrame(event.data);
            if (frame !== null) handlers.onFrame(frame);
        };
        socket.onerror = (): void => {
            if (active) handlers.onError('Lost connection to the dashboard composer.');
        };
        socket.onclose = (): void => {
            if (active) handlers.onClose();
        };

        return {
            close: (): void => {
                active = false;
                socket.close();
            },
        };
    }
}

function parseFrame(data: unknown): DashboardStreamFrame | null {
    if (typeof data !== 'string') return null;
    try {
        return JSON.parse(data) as DashboardStreamFrame;
    } catch {
        return null;
    }
}
